import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  Divider,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

const palette = {
  primary: '#1F3A5F',
  secondary: '#2C8C8C',
  accent: '#D6A75B',
  bg: '#F6F4EF',
  bgDark: '#0F1E2E',
  bgCard: '#FFFFFF',
  textDark: '#1a1a2e',
  textMuted: '#5a6872',
  textLight: '#E0E0E0',
  border: '#e8e4de',
  sectionAlt: '#F0EDE6',
};

function Privacy() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const sections = Array.from({ length: 11 }, (_, i) => {
    const n = i + 1;
    const section = {
      title: t(`privacy_page.s${n}_title`),
      content: t(`privacy_page.s${n}_content`, { defaultValue: '' }) || null,
    };
    const subs = t(`privacy_page.s${n}_subsections`, { returnObjects: true, defaultValue: null });
    if (subs && Array.isArray(subs)) {
      section.subsections = subs;
    }
    return section;
  });

  return (
    <Box sx={{ bgcolor: palette.bg, minHeight: '100vh' }}>
      <Box
        sx={{
          bgcolor: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${palette.border}`,
          py: 1.5,
          position: 'sticky',
          top: 0,
          zIndex: 1100,
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box
              component="img"
              src="/images/landing/logo-dark.webp"
              alt="URStore"
              sx={{ height: 36, cursor: 'pointer' }}
              onClick={() => navigate('/')}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/')}
                sx={{ color: palette.textMuted }}
              >
                {t('btn.back_to_home')}
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate('/apply')}
                sx={{
                  bgcolor: palette.accent,
                  color: palette.bgDark,
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 2.5,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#c4963e' },
                }}
              >
                {t('privacy_page.free_trial_btn')}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Box sx={{ bgcolor: palette.bgDark, color: 'white', py: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h2" fontWeight={800} sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, mb: 2 }}>
            {t('privacy_page.title')}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.7, fontWeight: 400 }}>
            {t('privacy_page.subtitle')}
          </Typography>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 4, md: 6 } }}>
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{
              bgcolor: palette.bgCard,
              border: `1px solid ${palette.border}`,
              borderRadius: 3,
              p: { xs: 3, md: 5 },
              boxShadow: '0 20px 60px rgba(0,0,0,0.06)',
            }}
          >
            {sections.map((section, index) => (
              <Box key={index}>
                {index > 0 && <Divider sx={{ my: 3, borderColor: palette.border }} />}
                <Typography variant="h6" fontWeight={700} sx={{ color: palette.primary, mb: 1.5 }}>
                  {section.title}
                </Typography>

                {section.content && (
                  <Typography variant="body1" sx={{ color: palette.textMuted, lineHeight: 1.9 }}>
                    {section.content}
                  </Typography>
                )}

                {section.subsections &&
                  section.subsections.map((sub, subIdx) => (
                    <Box key={subIdx} sx={{ mb: subIdx < section.subsections.length - 1 ? 2 : 0, mt: 1.5 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ color: palette.textDark, mb: 0.5 }}>
                        {sub.subtitle}
                      </Typography>
                      <Typography variant="body1" sx={{ color: palette.textMuted, lineHeight: 1.9 }}>
                        {sub.text}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            ))}

            <Divider sx={{ my: 3, borderColor: palette.border }} />
            <Typography variant="body2" sx={{ color: palette.textMuted, textAlign: 'center', mt: 2 }}>
              {t('privacy_page.last_updated')}
            </Typography>
          </Paper>
        </Container>
      </Box>

      <Box sx={{ bgcolor: palette.bgDark, borderTop: '1px solid rgba(255,255,255,0.06)', py: 3 }}>
        <Container maxWidth="lg">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)' }}>
              © 2026 URStore. All rights reserved.
            </Typography>
            <Button
              size="small"
              onClick={() => navigate('/')}
              sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'rgba(255,255,255,0.7)' } }}
            >
              {t('btn.back_to_home')}
            </Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

export default Privacy;
